import { useUpdate } from '@montel/montelpro-shared-components/hooks';
import {
	FunctionComponent,
	createContext,
	useState,
	useContext,
	ReactNode,
} from 'react';

interface Event {
	name: EventName;
	payload?: object;
	triggered: Date;
}

interface Events {
	current: Event | null;
	history: Event[];
}

type EventName = 'newIndicativePrice' | 'toggledIndicativePrice';

interface EmitResult {
	success: boolean;
	errorReason: 'NonExistingEventType' | 'ProviderNotReady' | null;
}

interface IEventContext {
	newIndicativePrice: Events;
	toggledIndicativePrice: Events;
	emit: (eventName: EventName) => EmitResult;
}

export const useEvents = (): IEventContext => useContext(EventContext);

export const EventContext = createContext<IEventContext>({
	newIndicativePrice: {
		current: null,
		history: [],
	},
	toggledIndicativePrice: {
		current: null,
		history: [],
	},
	emit: () => ({ errorReason: 'ProviderNotReady', success: false }),
});

export interface TypedEvents {
	newIndicativePrice: Events;
	toggledIndicativePrice: Events;
}

export const EventProvider: FunctionComponent<{
	children: ReactNode | ReactNode[];
}> = ({ children }) => {
	const [events, setEvents] = useState<Event[]>([]);
	const [typedEvents, setTypedEvents] = useState<TypedEvents>({
		newIndicativePrice: {
			current: null,
			history: [],
		},
		toggledIndicativePrice: {
			current: null,
			history: [],
		},
	});

	useUpdate(() => {
		const latest = events[events.length - 1];
		if (!latest) return;

		const newHistory =
			typedEvents[latest.name as keyof TypedEvents].history;
		newHistory.push(latest);

		setTypedEvents({
			...typedEvents,
			[latest.name]: {
				current: latest,
				history: newHistory,
			},
		});
	}, [events]);

	const emit = (eventName: EventName, payload?: object): EmitResult => {
		if (!typedEvents[eventName as keyof TypedEvents]) {
			return {
				success: false,
				errorReason: 'NonExistingEventType',
			};
		}

		setEvents([
			...events,
			{
				triggered: new Date(Date.now()),
				payload: payload,
				name: eventName,
			},
		]);

		return {
			success: true,
			errorReason: null,
		};
	};

	return (
		<EventContext.Provider
			value={{
				newIndicativePrice: typedEvents.newIndicativePrice,
				toggledIndicativePrice: typedEvents.toggledIndicativePrice,
				emit,
			}}
		>
			{children}
		</EventContext.Provider>
	);
};
